import { $createLinkNode } from "@lexical/link";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $createTextNode, $getRoot, $isTextNode, COMMAND_PRIORITY_LOW, LexicalNode, PASTE_COMMAND, TextNode } from "lexical";
import { useEffect } from "react";
import { $createStockNode } from "../nodes/StockNode";

interface NodeWithChildren {
  getChildren: () => LexicalNode[];
}

function hasChildren(node: LexicalNode): node is LexicalNode & NodeWithChildren {
  const maybeNode = node as Partial<NodeWithChildren>;
  return typeof maybeNode.getChildren === "function";
}

/**
 * 粘贴变换插件
 *
 * 功能：
 * - 监听 PASTE_COMMAND 并在粘贴后自动转换 $SYMBOL 和 #tag
 * - 避免在 SELECTION_CHANGE_COMMAND 中转换 URL（防止无限循环）
 */
export function PasteTransformPlugin(): null {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    // 处理粘贴命令
    const removePasteCommand = editor.registerCommand(
      PASTE_COMMAND,
      () => {
        // 使用 setTimeout 确保在粘贴内容被插入后执行
        setTimeout(() => {
          editor.update(() => {
            convertEntitiesInSelection();
          });
        }, 0);
        return false; // 让默认的粘贴处理继续执行
      },
      COMMAND_PRIORITY_LOW,
    );

    return () => {
      removePasteCommand();
    };
  }, [editor]);

  return null;
}

/**
 * 在整个文档中转换所有 $SYMBOL、#tag 和 URL
 *
 * 策略：
 * 1. 遍历所有初始的文本节点
 * 2. 对于每个文本节点，找到第一个实体并替换
 * 3. 重新搜索，直到该节点中没有更多实体
 * 4. 使用 setTimeout 避免阻塞 UI
 * 5. 防止重复转换已处理的内容
 */
function convertEntitiesInSelection(): void {
  const root = $getRoot();
  const initialTextNodes: TextNode[] = [];
  const processedKeys = new Set<string>(); // 防止重复处理

  // 收集所有初始文本节点
  const collectNodes = (node: LexicalNode) => {
    if ($isTextNode(node)) {
      // 跳过 LinkNode 内的文本节点
      const parent = node.getParent();
      if (parent && parent.getType() === "link") {
        return;
      }
      initialTextNodes.push(node);
    } else if (hasChildren(node)) {
      // 只对支持 getChildren() 的节点进行递归
      const children = node.getChildren();
      if (Array.isArray(children)) {
        children.forEach((child: LexicalNode) => {
          collectNodes(child);
        });
      }
    }
  };

  root.getChildren().forEach((node: LexicalNode) => {
    collectNodes(node);
  });

  // 对每个初始节点进行处理
  initialTextNodes.forEach((initialNode) => {
    // 跳过已处理的节点
    if (processedKeys.has(initialNode.getKey())) {
      return;
    }
    processedKeys.add(initialNode.getKey());

    processTextNode(initialNode, processedKeys);
  });
}

/**
 * 处理单个文本节点，逐步替换其中的所有实体
 */
function processTextNode(initialNode: TextNode, processedKeys: Set<string>): void {
  // 获取当前节点（可能在之前的替换中已经被分割）
  let currentNode: TextNode = initialNode;

  // 最多处理 100 次以避免无限循环
  const maxIterations = 100;
  let iterations = 0;

  while (iterations < maxIterations) {
    iterations++;

    // 检查节点是否还有效
    if (!$isTextNode(currentNode) || !currentNode.isAttached()) {
      break;
    }

    const text = currentNode.getTextContent();

    // 检查 Stock: $NVDA, $nvda, $BRK.A, $brk.b （匹配任意位置）
    const stockMatch = text.match(/\$([a-zA-Z]{1,5}(?:\.[a-zA-Z]{1,2})?)/);

    // Check Tag: #AI, #测试, #标签1 (matching at any position)
    // Note: Tag auto-conversion is DISABLED per SSOT requirement.
    // Users must select tags via LexicalSuggestionsPlugin which ensures tagId is populated.
    const tagMatch = null; // Disabled: /#([^\s#$.,!?;:'"()[\]{}]+)/;

    // 检查 URL: https://example.com, http://example.com
    const urlMatch = text.match(/(https?:\/\/[^\s]+)/);

    if (!stockMatch && !tagMatch && !urlMatch) {
      // 没有找到实体，检查后续的文本节点
      const nextSibling = currentNode.getNextSibling();
      if ($isTextNode(nextSibling) && !processedKeys.has(nextSibling.getKey())) {
        currentNode = nextSibling;
        continue;
      }
      // 需要继续在文档中查找下一个文本节点
      break;
    }

    // 执行替换 - 优先级：URL > Stock > Tag
    let matchStart = 0;
    let matchLength = 0;
    let newNode: LexicalNode | null = null;

    // 找到最早出现的匹配
    const matches = [
      { type: "url", match: urlMatch },
      { type: "stock", match: stockMatch },
      { type: "tag", match: tagMatch },
    ].filter((m) => m.match && m.match.index !== undefined);

    if (matches.length === 0) {
      const nextSibling = currentNode.getNextSibling();
      if ($isTextNode(nextSibling)) {
        currentNode = nextSibling;
        continue;
      }
      break;
    }

    // 找到最早出现的匹配
    matches.sort((a, b) => (a.match?.index || 0) - (b.match?.index || 0));
    const firstMatch = matches[0];

    if (firstMatch.type === "url" && firstMatch.match && firstMatch.match.index !== undefined) {
      matchStart = firstMatch.match.index;
      matchLength = firstMatch.match[0].length;
      const url = firstMatch.match[0];
      // 直接创建 LinkNode，包含 URL 文本
      const linkTextNode = $createTextNode(url);
      const linkNode = $createLinkNode(url);
      linkNode.append(linkTextNode);
      newNode = linkNode;
    } else if (firstMatch.type === "stock" && firstMatch.match && firstMatch.match.index !== undefined) {
      const symbol = firstMatch.match[1].toUpperCase();
      matchStart = firstMatch.match.index;
      matchLength = firstMatch.match[0].length;
      newNode = $createStockNode(symbol, "neutral", null);
    }
    // Tag auto-conversion removed - users must use LexicalSuggestionsPlugin

    if (newNode) {
      try {
        // 标记新节点为已处理，防止重复转换
        processedKeys.add(newNode.getKey());

        const beforeText = text.slice(0, matchStart);
        const afterText = text.slice(matchStart + matchLength);

        if (beforeText.length > 0 && afterText.length > 0) {
          // 前后都有文本，需要创建三个节点
          const beforeNode = $createTextNode(beforeText);
          const afterNode = $createTextNode(afterText);

          // 标记新节点
          processedKeys.add(beforeNode.getKey());
          processedKeys.add(afterNode.getKey());

          currentNode.insertBefore(beforeNode);
          currentNode.replace(newNode);
          newNode.insertAfter(afterNode);

          // 继续处理 afterNode
          currentNode = afterNode;
        } else if (beforeText.length > 0) {
          // 只有前文
          const beforeNode = $createTextNode(beforeText);
          processedKeys.add(beforeNode.getKey());

          currentNode.insertBefore(beforeNode);
          currentNode.replace(newNode);

          // 查找下一个文本节点
          const nextSibling = newNode.getNextSibling();
          if ($isTextNode(nextSibling) && !processedKeys.has(nextSibling.getKey())) {
            currentNode = nextSibling;
          } else {
            break;
          }
        } else if (afterText.length > 0) {
          // 只有后文
          currentNode.replace(newNode);
          const afterNode = $createTextNode(afterText);
          processedKeys.add(afterNode.getKey());
          newNode.insertAfter(afterNode);
          currentNode = afterNode;
        } else {
          // 完全替换
          currentNode.replace(newNode);

          // 查找下一个文本节点
          const nextSibling = newNode.getNextSibling();
          if ($isTextNode(nextSibling) && !processedKeys.has(nextSibling.getKey())) {
            currentNode = nextSibling;
          } else {
            break;
          }
        }
      } catch (e) {
        console.error("Error during replacement:", e);
        break;
      }
    }
  }
}
